import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useToast } from "@/hooks/useToast";
import { resourcesApi } from "@/services/api";
import type { ProgramResource, ResourceSource } from "@/services/api";
import { validateResourceFile } from "@/utils/documentValidation";

/**
 * The resource list for one PAB or Expert Program.
 *
 * Shared between the Resources panel and the tab's count badge via one query
 * key, so the badge costs no extra request — React Query dedupes them. Do not
 * add a second query for the count.
 *
 * `programId` is the numeric programs.id; a PAB session id works unchanged.
 */
export const resourcesQueryKey = (programId: number | null | undefined) =>
  ["program-resources", programId] as const;

export const useProgramResources = (programId: number | null | undefined, enabled = true) =>
  useQuery({
    queryKey: resourcesQueryKey(programId),
    queryFn: () => resourcesApi.list(programId as number),
    enabled: enabled && !!programId,
    staleTime: 60 * 1000,
  });

/** Just the count, for the tab badge. */
export const useProgramResourceCount = (
  programId: number | null | undefined,
  enabled = true,
): number => {
  const { data } = useProgramResources(programId, enabled);
  return data?.total ?? 0;
};

/** Server messages are surfaced verbatim: they say WHICH rule failed (wrong
 *  type, too large, bytes don't match the extension), which a generic
 *  "upload failed" cannot. */
const detailOf = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback;

/**
 * Upload a batch to a programme id known only at call time — used when files
 * were picked BEFORE the programme row existed and are flushed the moment it
 * does (the Overview step's prerequisites field).
 *
 * Returns the per-file problems instead of toasting them, because the caller is
 * mid-save and decides how to report. Uploads are SEQUENTIAL for the same
 * reason as the hook below: writes are rate-limited at 25/minute per IP.
 */
export async function uploadResourceFiles(
  programId: number,
  files: File[],
  source: ResourceSource = "RESOURCE",
): Promise<string[]> {
  const problems: string[] = [];
  for (const file of files) {
    const invalid = validateResourceFile(file);
    if (invalid) {
      problems.push(invalid);
      continue;
    }
    try {
      await resourcesApi.upload(programId, file, { source });
    } catch (err: unknown) {
      problems.push(detailOf(err, `Could not upload "${file.name}".`));
    }
  }
  return problems;
}

/**
 * Upload / download / delete for one programme's resources.
 *
 * Extracted so the Resources panel and the inline prerequisites field share ONE
 * implementation of the fiddly parts — client-side validation, sequential
 * uploads, surfacing the server's rejection reason, and fetching a presigned
 * link at click time. Two copies of that would drift.
 */
export const useResourceUploader = (
  programId: number | null | undefined,
  source: ResourceSource = "RESOURCE",
) => {
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToast();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { data, isLoading } = useProgramResources(programId);
  const resources = useMemo(() => data?.resources ?? [], [data]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: resourcesQueryKey(programId) });
  }, [queryClient, programId]);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => resourcesApi.upload(programId as number, file, { source }),
    onSuccess: (created) => {
      invalidate();
      // Always name the file. The first-upload case used to pass an empty
      // body, which rendered a bare "File uploaded" title with nothing under
      // it -- easy to miss, and it never confirmed WHICH file landed when a
      // batch was dropped in.
      showSuccessToast(
        created.version > 1
          ? `${created.filename} saved as version ${created.version}.`
          : `${created.filename} has been uploaded.`,
        "File uploaded",
      );
    },
    onError: (err: unknown) =>
      showErrorToast(detailOf(err, "Could not upload that file. Please try again."), "Upload failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (resourceId: number) => resourcesApi.delete(programId as number, resourceId),
    onSuccess: () => {
      invalidate();
      showSuccessToast("", "File deleted");
    },
    onError: (err: unknown) =>
      showErrorToast(detailOf(err, "Could not delete that file."), "Delete failed"),
  });

  /** Validates client-side first, then uploads SEQUENTIALLY — writes are
   *  rate-limited at 25/minute per IP, so a dropped batch of ten firing at once
   *  would trip the limit and fail most of the batch. */
  const uploadFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length || !programId) return;
      for (const file of Array.from(files)) {
        const problem = validateResourceFile(file);
        if (problem) {
          // Report and skip — one bad file shouldn't abandon the rest.
          showErrorToast(problem, "File not accepted");
          continue;
        }
        try {
          await uploadMutation.mutateAsync(file);
        } catch {
          // Already surfaced by onError; carry on with the remaining files.
        }
      }
    },
    [programId, uploadMutation, showErrorToast],
  );

  const download = useCallback(
    async (resource: ProgramResource) => {
      if (!programId) return;
      setDownloadingId(resource.id);
      try {
        // Fetched at click time: the link is presigned and expires in minutes,
        // so it is never held in state or prefetched with the list.
        const { url } = await resourcesApi.getDownloadLink(programId, resource.id);
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err: unknown) {
        showErrorToast(detailOf(err, "Could not open that file."), "Download failed");
      } finally {
        setDownloadingId(null);
      }
    },
    [programId, showErrorToast],
  );

  return {
    resources,
    isLoading,
    uploadFiles,
    isUploading: uploadMutation.isPending,
    download,
    downloadingId,
    deleteResource: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
